import { Button } from 'components/elements/Button'
import TextField from 'components/TextField'
import formatDistanceToNow from 'date-fns/formatDistanceToNow'
import CLIENT_SETTINGS from 'lib/client_settings'
import React from 'react'
import { CombinedProps, fetchDataGApi } from '../fetcher'


type JobCatalogParam = {
  name: string,
  type: string,
  required?: boolean,
  description: string,
}

type JobCatalogExample = {
  label: string,
  jobData: string,
  command: string,
  // Present instead of a usable command when the payload contains whitespace: the job runner
  // splits the command on whitespace, so those have to be queued from mongosh.
  pasteable?: boolean,
  mongoshCommand?: string,
}

type JobCatalogEntry = {
  name: string,
  title: string,
  description: string,
  risk?: 'destructive'|'wideReaching'|'slow',
  // Where the job is offered; omitted means everywhere. The API already leaves out jobs that do
  // not belong to its environment, this keeps a staging-only job off a production dashboard too.
  environments?: string[],
  // Only trusted operators may queue a restricted job on this environment; canRun says whether
  // the signed-in admin is one. Older APIs send neither, which reads as open.
  restricted?: boolean,
  canRun?: boolean,
  params: JobCatalogParam[],
  examples: JobCatalogExample[],
}

type UsageEntry = {
  name: string,
  runs: number,
  lastRun?: string,
  lastParams?: string,
  errors: number,
}

type CatalogRes = {
  jobs: JobCatalogEntry[],
  undocumented: string[],
  allNames: string[],
}

type UsageRes = {
  jobs: UsageEntry[],
}

type JobCatalogPropsIn = {
  onPick: (command: string, risk?: string) => void,
}
/**
 * fetchDataGApi types its Component with only four of CombinedProps' six arguments, so the second
 * endpoint's payload arrives as DataRes<{}>. Asserted at the one place it is read rather than by
 * widening the shared fetcher, which every admin view depends on.
 */
type JobCatalogProps = CombinedProps<JobCatalogPropsIn, 'onPick', {}, CatalogRes>

const RISK_LABELS: Record<string, {label: string, className: string}> = {
  destructive: {label: 'destructive', className: 'bg-coral text-white'},
  wideReaching: {label: 'affects every workspace', className: 'bg-team text-team-text'},
  slow: {label: 'long-running', className: 'bg-lake text-white'},
}

/**
 * The runner injects these into jobData before handing it to a job, so they come back in the
 * logged parameters. They are not something anyone types.
 */
function stripInjectedParams(params: string) {
  try {
    const parsed = JSON.parse(params)
    const cleaned = Object.keys(parsed)
      .filter(k => !k.startsWith('_'))
      .reduce((acc, k) => ({...acc, [k]: parsed[k]}), {})
    return JSON.stringify(cleaned)
  } catch {
    return params
  }
}

function buildMongoshCommand(name: string, jobData: string) {
  return `db.jobManualRuns.insertOne({jobName: "${name}", jobData: ${JSON.stringify(jobData)}, `
    + 'isRunning: false, createdAt: new Date()})'
}

function isAvailableHere(entry: JobCatalogEntry) {
  return !entry.environments || entry.environments.includes(CLIENT_SETTINGS.public.environment)
}

/** Shown on jobs that exist only outside production, so nobody hunts for them there. */
function EnvironmentBadge({environments}: {environments?: string[]}) {
  if (!environments || environments.includes('production')) return null
  return <span className="ml-2 rounded border border-yellow-200 bg-yellow-100 px-2 py-0.5 text-xs font-bold uppercase text-deepgray">
    {environments.includes('staging') ? 'staging only' : environments.join(', ')}
  </span>
}

function RestrictedBadge({restricted}: {restricted?: boolean}) {
  if (!restricted) return null
  return <span className="ml-2 rounded bg-deepgray px-2 py-0.5 text-xs font-bold uppercase text-white">
    🔒 trusted operators
  </span>
}

function RiskBadge({risk}: {risk?: string}) {
  const meta = risk && RISK_LABELS[risk]
  if (!meta) return null
  return <span className={`ml-2 rounded px-2 py-0.5 text-xs font-bold uppercase ${meta.className}`}>
    {meta.label}
  </span>
}

/** A command the runner cannot take is offered as a mongosh insert to copy instead. */
function CommandRow(props: {
  label: string, command: string, mongosh?: string, locked?: boolean, onPick: () => void,
}) {
  const [copied, setCopied] = React.useState(false)

  const copy = () => {
    navigator.clipboard.writeText(props.mongosh || '').then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return <div className="flex flex-row items-start justify-between gap-4 py-1">
    <div className="flex-1">
      <div className="text-sm text-deepgray">{props.label}</div>
      <code className="block break-all font-mono text-xs text-medgray">
        {props.mongosh || props.command}
      </code>
    </div>
    {props.locked
      ? <span className="text-xs text-medgray">Ask an engineer</span>
      : props.mongosh
        ? <Button type="default" size="small" text={copied ? 'Copied' : 'Copy'} onClick={copy} />
        : <Button type="secondary" size="small" text="Use" onClick={props.onPick} />}
  </div>
}

function JobEntry(props: {
  entry: JobCatalogEntry,
  usage?: UsageEntry,
  onPick: (command: string, risk?: string) => void,
}) {
  const {entry, usage} = props
  const [open, setOpen] = React.useState(false)

  return <div className="border-b border-solid border-gray-200 py-3">
    <div className="flex cursor-pointer flex-row items-center justify-between" onClick={() => setOpen(!open)}>
      <div>
        <span className="font-bold text-deepgray">{entry.title}</span>
        <RiskBadge risk={entry.risk} />
        <EnvironmentBadge environments={entry.environments} />
        <RestrictedBadge restricted={entry.restricted} />
        <div className="font-mono text-xs text-medgray">{entry.name}</div>
      </div>
      {usage && <div className="text-right text-xs text-medgray">
        {usage.runs} run{usage.runs === 1 ? '' : 's'}
        {usage.errors > 0 && <span className="text-coral"> · {usage.errors} failed</span>}
        {usage.lastRun && <div>{formatDistanceToNow(new Date(usage.lastRun))} ago</div>}
      </div>}
    </div>

    {open && <div className="mt-3">
      <div className="text-sm text-medgray">{entry.description}</div>
      {entry.canRun === false && <div className="mt-2 text-sm font-bold text-coral">
        Only trusted operators can run this job here. Ask an engineer to run it for you.
      </div>}

      {entry.params.length > 0 && <table className="mt-3 w-full text-xs">
        <tbody>
          {entry.params.map(param => <tr key={param.name}>
            <td className="py-1 pr-3 align-top font-mono text-deepgray">{param.name}</td>
            <td className="py-1 pr-3 align-top text-medgray">{param.type}</td>
            <td className="py-1 pr-3 align-top text-medgray">
              {param.required ? <span className="font-bold text-coral">required</span> : 'optional'}
            </td>
            <td className="py-1 align-top text-medgray">{param.description}</td>
          </tr>)}
        </tbody>
      </table>}

      <div className="mt-3">
        {entry.examples.map(example => <CommandRow
          key={example.label}
          label={example.label}
          command={example.command}
          mongosh={example.pasteable === false ? example.mongoshCommand : undefined}
          locked={entry.canRun === false}
          onPick={() => props.onPick(example.command, entry.risk)}
        />)}
      </div>

      {usage && usage.lastParams && <CommandRow
        label="Parameters used last time"
        command={`run_job ${entry.name} ${stripInjectedParams(usage.lastParams)}`}
        mongosh={/\s/.test(stripInjectedParams(usage.lastParams))
          ? buildMongoshCommand(entry.name, stripInjectedParams(usage.lastParams))
          : undefined}
        locked={entry.canRun === false}
        onPick={() => props.onPick(
          `run_job ${entry.name} ${stripInjectedParams(usage.lastParams || '{}')}`,
          entry.risk,
        )}
      />}
    </div>}
  </div>
}

const COLLAPSED_COUNT = 3

class JobCatalog extends React.Component<JobCatalogProps, {
  search: string,
  showAll: boolean,
  expandedMostUsed: boolean,
  expandedRest: boolean,
}> {
  constructor(props: JobCatalogProps) {
    super(props)
    this.state = {search: '', showAll: false, expandedMostUsed: false, expandedRest: false}
  }

  /** Sections open to the first few entries; the rest is one click away, and folds back. */
  renderSection(
    label: string,
    entries: JobCatalogEntry[],
    expanded: boolean,
    toggle: () => void,
    usageByName: Map<string, UsageEntry>,
  ) {
    if (entries.length === 0) return null
    const shown = expanded ? entries : entries.slice(0, COLLAPSED_COUNT)
    const hidden = entries.length - shown.length

    return <div className="mt-4">
      <div className="text-xs font-bold uppercase text-medgray">{label} ({entries.length})</div>
      {shown.map(entry => <JobEntry
        key={entry.name} entry={entry} usage={usageByName.get(entry.name)} onPick={this.props.onPick}
      />)}
      {(hidden > 0 || expanded) && <div
        className="mt-2 cursor-pointer text-xs font-bold text-lake"
        onClick={toggle}
      >
        {expanded ? 'Show less' : `Show ${hidden} more`}
      </div>}
    </div>
  }

  render() {
    const {fData, fData2, onPick} = this.props
    if (fData.resType === 'loading') return <div className="text-medgray">Loading job catalog…</div>
    if (fData.resType === 'error') return <div className="text-coral">Could not load the job catalog.</div>

    const usageJobs = fData2 && fData2.resType === 'success' ? (fData2.data as UsageRes).jobs : []
    const usageByName = new Map<string, UsageEntry>(
      usageJobs.map(u => [u.name, u] as [string, UsageEntry]),
    )

    const search = this.state.search.trim().toLowerCase()
    const matches = (entry: JobCatalogEntry) => !search
      || entry.name.toLowerCase().includes(search)
      || entry.title.toLowerCase().includes(search)
      || entry.description.toLowerCase().includes(search)

    // A-Z by job name: the name is what gets typed and searched for, and it matches the ordering
    // of the undocumented list, which the API already returns sorted.
    const documented = fData.data.jobs
      .filter(isAvailableHere)
      .filter(matches)
      .sort((a, b) => a.name.localeCompare(b.name))
    // Ranked by how often they have actually been run, so the list reflects real use rather than
    // an opinion about what matters.
    const mostUsed = documented
      .filter(entry => usageByName.has(entry.name))
      .sort((a, b) => (usageByName.get(b.name)!.runs) - (usageByName.get(a.name)!.runs))
      .slice(0, 5)
    // Sorted already; filtering preserves it.
    const rest = documented.filter(entry => !mostUsed.includes(entry))
    const undocumented = fData.data.undocumented.filter(n => !search || n.toLowerCase().includes(search))

    return <div className="rounded-lg border border-solid border-gray-200 bg-white p-4">
      <div className="flex flex-row items-center justify-between">
        <h2 className="font-bold text-deepgray">Job catalog</h2>
        <span className="text-xs text-medgray">
          {fData.data.jobs.length} documented · {fData.data.allNames.length} runnable
        </span>
      </div>

      <div className="mt-3">
        <TextField
          value={this.state.search}
          placeholder="Search jobs…"
          isClearable
          onChange={v => this.setState({search: v})}
        />
      </div>

      {this.renderSection(
        'Most used', mostUsed, this.state.expandedMostUsed,
        () => this.setState({expandedMostUsed: !this.state.expandedMostUsed}), usageByName,
      )}

      {this.renderSection(
        'All documented jobs', rest, this.state.expandedRest,
        () => this.setState({expandedRest: !this.state.expandedRest}), usageByName,
      )}

      {documented.length === 0 && <div className="mt-4 text-sm text-medgray">
        No documented job matches “{this.state.search}”.
      </div>}

      <div className="mt-4">
        <div
          className="cursor-pointer text-xs font-bold uppercase text-medgray"
          onClick={() => this.setState({showAll: !this.state.showAll})}
        >
          {this.state.showAll ? '▾' : '▸'} Undocumented jobs ({undocumented.length})
        </div>
        {this.state.showAll && <div className="mt-2 flex flex-row flex-wrap gap-2">
          {undocumented.map(name => <span
            key={name}
            className="cursor-pointer rounded bg-alabastergray px-2 py-1 font-mono text-xs text-medgray"
            onClick={() => onPick(`run_job ${name} {}`)}
          >
            {name}
          </span>)}
        </div>}
      </div>
    </div>
  }
}

export default fetchDataGApi<JobCatalogPropsIn, 'onPick', {}, CatalogRes, 'onPick', UsageRes>(
  {
    getEndpoint: () => '/api/v1/admin/jobs/catalog',
    getDataFields: [],
    getEndpoint2: () => '/api/v1/admin/jobs/usage',
    getData2Fields: [],
    paramsFromCmpDefault: {},
  },
  JobCatalog,
)
